/*
 * ******************************************************************
 * Copyright (c) 2021-2026 Broadcom. All Rights Reserved.
 * Broadcom Confidential. The term "Broadcom" refers to Broadcom Inc.
 * and/or its subsidiaries.
 * ******************************************************************
 */

import { CUSTOM_ELEMENTS_SCHEMA, NgModule, Provider } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { RouterModule } from "@angular/router";

// Libraries & Modules
import { ClarityModule } from "@clr/angular";
import { CspComponentsModule } from "@vmw/csp-ngx-components";
import { VIPModule } from "@vmw/ngx-vip";
import { VmwNgxVerticalNavUtilsModule } from "@vmw/ngx-utils/vertical-nav-utils";
import { NgxJsonViewerModule } from "ngx-json-viewer";
import { VmwComponentsModule } from "@vmw/ngx-components";
import { AppfxWorkflowsModule } from "@vmw/appfx/workflow";
import { HighchartsChartComponent } from "highcharts-angular";

// Pipes
import { FileSizePipe } from "./pipes/file-size.pipe";

// Providers
import { IconService } from "./services";
import { VmwClarityThemeService } from "@vmw/ngx-utils";

// Directives
import { DrClipboardIconComponent, ShowTitleDirective } from "./directives/dr-show-title";
import { DrSetAriaCurrentDirective } from "./directives/dr-aria-current/dr-set-aria-current.directive";
import { DrNavGroupTriggerLabelDirective } from "./directives/dr-nav-group-trigger-label/dr-nav-group-trigger-label.directive";
import { RequiredPrivilegesDirective, RequiredRoleDirective } from "./directives/rbac/";
import { DialogAnchorDirective } from "./components/dialog/dialog-anchor/dialog-anchor.directive";

// Components
import { Error403Component } from "./components/error403/error403.component";
import { Error404Component } from "./components/error404/error404.component";
import { SummaryGridComponent } from "./components/summary-grid/summary-grid.component";
import { SummaryGridItemComponent } from "./components/summary-grid/summary-grid-item/summary-grid-item.component";
import { SummaryGridTotalComponent } from "./components/summary-grid/summary-grid-total/summary-grid-total.component";
import { InitialStateComponent } from "./components/initial-state/initial-state.component";
import { LayoutComponent } from "./components/layout/layout.component";
import { ConfirmationDialog } from "./components/dialog/confirmation-dialog/confirmation-dialog";
import { MessageDialog } from "./components/dialog/message-dialog/message-dialog";
import { VerticalNavComponent } from "./components/navigation/vertical-nav.component";
import { PropertyValueComponent } from "./components/property-value/property-value.component";
import { LoadingSpinnerComponent } from "./components/loading-spinner/loading-spinner.component";
import { PageHeaderComponent } from "./components/page-header/page-header.component";
import { DatagridDateFilter } from "./components/filters/datagrid-date-filter/datagrid-date-filter.component";
import { VersionsComponent } from "./components/versions/versions.component";
import { ErrorMsgComponent } from "./components/loading-error/error-msg.component";
import { UnitsFormatDirective } from "./directives/units-format/units-format.directive";
import { DownloadJsonComponent } from "./components/download-json/download-json.component";
import { GlobalErrorComponent } from "./components/global-error/global-error.component";
import { VmwTaskPanelWrapComponent } from "./components/vmw-task-panel-wrapper/vmw-task-panel-wrap.component";
import { VmwTaskWrapComponent } from "./components/vmw-task-panel-wrapper/task-wrap.component";
import { RadioCardComponent } from "./components/radio-card/radio-card.component";
import { InfoCardComponent } from "./components/info-card/info-card.component";
import { DatagridEnumFilter } from "./components/filters/datagrid-enum-filter/datagrid-enum-filter.component";
import { DatagridCheckboxFilter } from "./components/filters/datagrid-checkbox-filter/datagrid-checkbox-filter.component";
import { DatagridRadioFilter } from "./components/filters/datagrid-radio-filter/datagrid-radio-filter.component";
import { DatagridSingleDateFilter } from "./components/filters/datagrid-single-date-filter/datagrid-single-date-filter.component";
import { DatagridCustomStringFilter } from "./components/filters/datagrid-custom-string-filter/datagrid-custom-string-filter.component";
import { DatagridSearchableFilter } from "./components/filters/datagrid-searchable-filter/datagrid-searchable-filter.component";
import { SettingToggleComponent } from "./components/setting-toggle/setting-toggle.component";
import { VerticalNavItemComponent } from "./components/navigation/vertical-nav-item/vertical-nav-item.component";
import { BreadcrumbComponent } from "./components/breadcrumb/breadcrumb.component";
import { DrStringActionComponent } from "./components/dr-string-action/dr-string-action.component";
import { HorizontalBarChartComponent } from "./components/horizontal-bar-chart/horizontal-bar-chart.component";
import { DgExportComponent } from "@shared/components/dg-export/dg-export.component";
import { CsvHelperService } from "@shared/services/export/csv-helper.service";
import { IconLabelComponent } from "@shared/components/icon-label/icon-label.component";

const MODULES = [
    CommonModule,
    FormsModule,
    RouterModule,
    ClarityModule,
    CspComponentsModule,
    VIPModule,
    VmwComponentsModule,
    VmwNgxVerticalNavUtilsModule,
    NgxJsonViewerModule,
    AppfxWorkflowsModule,
    HighchartsChartComponent
];

const COMPONENTS = [
    Error403Component,
    Error404Component,
    SummaryGridComponent,
    SummaryGridItemComponent,
    SummaryGridTotalComponent,
    InitialStateComponent,
    LayoutComponent,
    MessageDialog,
    ConfirmationDialog,
    VerticalNavComponent,
    VerticalNavItemComponent,
    PropertyValueComponent,
    DatagridEnumFilter,
    DatagridCheckboxFilter,
    DatagridRadioFilter,
    DatagridDateFilter,
    DatagridSingleDateFilter,
    DatagridCustomStringFilter,
    DatagridSearchableFilter,
    DgExportComponent,
    LoadingSpinnerComponent,
    PageHeaderComponent,
    VersionsComponent,
    ErrorMsgComponent,
    UnitsFormatDirective,
    DownloadJsonComponent,
    GlobalErrorComponent,
    VmwTaskPanelWrapComponent,
    VmwTaskWrapComponent,
    RadioCardComponent,
    InfoCardComponent,
    SettingToggleComponent,
    BreadcrumbComponent,
    DrStringActionComponent,
    HorizontalBarChartComponent,
    IconLabelComponent
];

const DIRECTIVES = [
    ShowTitleDirective,
    DialogAnchorDirective,
    DrClipboardIconComponent,
    DrSetAriaCurrentDirective,
    DrNavGroupTriggerLabelDirective,
    RequiredPrivilegesDirective,
    RequiredRoleDirective
];

const PIPES = [
    FileSizePipe
];

const PROVIDERS: Provider[] = [
    IconService,
    VmwClarityThemeService,
    CsvHelperService
];

// @formatter:off
@NgModule({
    imports: [MODULES],
    declarations: [
        COMPONENTS,
        DIRECTIVES,
        PIPES,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    exports: [
        MODULES,
        COMPONENTS,
        DIRECTIVES,
        PIPES
    ],
    providers: [
        PROVIDERS
    ]
})
// @formatter:on
export class SharedModule {
}
